import cx from "classnames";
import Image from "next/image";
import Carousel, { Alignment } from "nuka-carousel";
import { useState } from "react";

import { useSettings } from "shared/SettingsProvider/useSettings";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { PictureCard } from "shared/product/ProductSection/ProductImages/PictureCard";
import { StrapiImage, StrapiImageData } from "ui/StrapiImage";

type ProductImagesProps = {
  className?: string;
};

export const ProductImages = ({ className }: ProductImagesProps) => {
  const { isBlackFriday } = useSettings();
  const { currentVariant, family } = useProductPageContext();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const hasUpfrontPriceDiscount =
    currentVariant?.originalUpfrontPrice != null &&
    currentVariant?.originalUpfrontPrice !== currentVariant?.upfrontPrice;
  const hasSubscriptionDiscount =
    currentVariant?.originalSubscriptionPrice != null &&
    currentVariant?.originalSubscriptionPrice !==
      currentVariant?.subscriptionPrice;
  const isBlackWeekProduct =
    currentVariant?.tagSlugs?.includes("black-week") ?? false;
  const isBlackFridayDiscount =
    isBlackFriday && isBlackWeekProduct && family.category === "available";

  const images = [
    currentVariant?.combinedPicture,
    currentVariant?.backPicture,
    currentVariant?.frontPicture,
  ].filter(Boolean) as StrapiImageData[];

  const handleClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  return (
    <div className={cx("grow max-w-960", className)}>
      {/* Mobile version */}
      <div className="relative lg:hidden">
        <Carousel
          className="mt-5"
          cellAlign={Alignment.Center}
          defaultControlsConfig={{
            nextButtonClassName: "hidden",
            prevButtonClassName: "hidden",
            pagingDotsContainerClassName: "flex gap-3 product-images-carousel",
            pagingDotsStyle: {
              marginBottom: "6px",
            },
          }}
        >
          {images?.map((image, index) => (
            <div key={index} className="flex justify-center items-center mb-12">
              <StrapiImage
                size="medium"
                image={image}
                className="w-64 h-64"
                loader={true}
                priority
                sizes="50vw"
              />
            </div>
          ))}
        </Carousel>

        {isBlackFridayDiscount && (
          <div className="absolute top-10 left-1/2 w-[153px] h-[91px]">
            <Image src="/images/christmas/product-sticker.webp" layout="fill" />
          </div>
        )}
      </div>

      {/* Desktop version */}
      <div className="hidden flex-col justify-center items-center text-2xs text-gray-4 sm:gap-10 sm:text-xs lg:flex lg:gap-16 lg:mt-6 2xs:gap-5">
        <StrapiImage
          size="medium"
          sizes="50vw"
          className={cx("w-[30rem] h-[30rem] my-24")}
          image={images?.[selectedImageIndex]}
          priority={true}
          loader={true}
        />
        <div className="flex gap-4">
          {images?.map((image, index) => (
            <PictureCard
              key={index}
              image={image}
              onClick={() => handleClick(index)}
              className={cx("cursor-pointer", {
                "bg-yellow/20": selectedImageIndex === index,
              })}
              loader={true}
            />
          ))}
        </div>
        {isBlackFridayDiscount && (
          <div className="absolute w-[181px] h-[107px] translate-x-44 -translate-y-48">
            <Image src="/images/christmas/product-sticker.webp" layout="fill" />
          </div>
        )}
      </div>

      {/* DAS mentions */}
      <div className="flex overflow-hidden gap-2 justify-center w-full text-2xs text-gray-4 sm:gap-10 sm:text-xs lg:gap-16 lg:mt-6 2xs:gap-5">
        {family?.dasValues?.map((dasValue, index) => (
          <p key={index} className="whitespace-nowrap">
            {dasValue}
          </p>
        ))}
      </div>
    </div>
  );
};
